import { useState, useEffect, useCallback } from 'react';

const useAntiCheat = (currentStep, navigate) => {
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  const [showTabWarning, setShowTabWarning] = useState(false);
  const [warningLevel, setWarningLevel] = useState('yellow');
  const [isTestCancelled, setIsTestCancelled] = useState(false);

  // Disable copy, cut, and paste
  useEffect(() => {
    const preventCopyPaste = (e) => {
      // Prevent copy, cut, paste events
      if (e.type === 'copy' || e.type === 'cut' || e.type === 'paste') {
        e.preventDefault();
        e.stopPropagation();
        
        // Show brief notification
        const notification = document.createElement('div');
        notification.textContent = 'Copy/Paste is disabled during the test';
        notification.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: #ef4444;
          color: white;
          padding: 12px 24px;
          border-radius: 8px;
          z-index: 9999;
          font-weight: 500;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        `;
        document.body.appendChild(notification);
        
        setTimeout(() => {
          notification.remove();
        }, 2000);
        
        return false;
      }
    };

    // Prevent keyboard shortcuts
    const preventKeyboardShortcuts = (e) => {
      // Prevent Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+A
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'c' || e.key === 'v' || e.key === 'x' || e.key === 'a') {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      }
    };

    // Prevent right-click context menu
    const preventContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    if (currentStep === 'reading' || currentStep === 'writing') {
      // Add event listeners
      document.addEventListener('copy', preventCopyPaste);
      document.addEventListener('cut', preventCopyPaste);
      document.addEventListener('paste', preventCopyPaste);
      document.addEventListener('keydown', preventKeyboardShortcuts);
      document.addEventListener('contextmenu', preventContextMenu);

      // Add CSS to prevent text selection
      document.body.style.userSelect = 'none';
      document.body.style.webkitUserSelect = 'none';
      document.body.style.msUserSelect = 'none';
      document.body.style.mozUserSelect = 'none';
    }

    return () => {
      // Cleanup
      document.removeEventListener('copy', preventCopyPaste);
      document.removeEventListener('cut', preventCopyPaste);
      document.removeEventListener('paste', preventCopyPaste);
      document.removeEventListener('keydown', preventKeyboardShortcuts);
      document.removeEventListener('contextmenu', preventContextMenu);
      
      // Restore text selection
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.body.style.msUserSelect = '';
      document.body.style.mozUserSelect = '';
    };
  }, [currentStep]);

  // Tab switch detection - IMMEDIATE CANCELLATION (no warnings)
  const handleVisibilityChange = useCallback(() => {
    if (document.hidden && (currentStep === 'reading' || currentStep === 'writing')) {
      console.log('Tab switch detected - TEST CANCELLED IMMEDIATELY');
      
      // Immediately cancel test - no warnings
      setIsTestCancelled(true);
      setShowTabWarning(true);
      
      // Navigate to dashboard after showing warning
      setTimeout(() => {
        navigate('/student/dashboard');
      }, 3000);
    }
  }, [currentStep, navigate]);

  useEffect(() => {
    if (currentStep === 'reading' || currentStep === 'writing') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      // Also detect when window loses focus
      window.addEventListener('blur', handleVisibilityChange);
      
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('blur', handleVisibilityChange);
      };
    }
  }, [currentStep, handleVisibilityChange]);

  return {
    tabSwitchCount,
    showTabWarning,
    warningLevel,
    isTestCancelled,
    setShowTabWarning
  };
};

export default useAntiCheat;